import { useEffect, useState } from 'react'
import { cn } from 'components/UI/cn'
import { DropdownMenuTrigger } from '@radix-ui/react-dropdown-menu'
import type { FilterStore } from 'components/Filters/FilterStore'
import {
  DropdownMenu,
  DropdownMenuContent,
} from 'components/UI/BaseDropdown/dropdown-base'
import { DatePicker } from 'components/FormUtils/DatePicker'
import FbButton from 'components/FbUI/FbButton'
import { FilterButton } from '../components/FilterButton'
import { FilterCloseButton } from '../components/FilterCloseButton'

export function DateRangeFilter({
  title,
  filterKey,
  filterStore,
}: {
  title: string
  filterKey: string
  filterStore: FilterStore
}) {
  const [open, setOpen] = useState(false)
  const {
    pendingRangeFilters,
    setPendingRangeFilters,
    clearPendingRangeFilter,
    registerFilterTitle,
  } = filterStore()
  const current = pendingRangeFilters?.[filterKey!]

  // Note: RangeFilter type uses number, but date filters use string values (YYYY-MM-DD)
  // This is a pre-existing type inconsistency in the FilterStore
  const [minDate, setMinDate] = useState<string | undefined>(
    current?.min != null ? String(current.min) : undefined
  )
  const [maxDate, setMaxDate] = useState<string | undefined>(
    current?.max != null ? String(current.max) : undefined
  )
  const [error, setError] = useState<string | null>(null)

  // Sync local state when current filter changes
  useEffect(() => {
    setMinDate(current?.min != null ? String(current.min) : undefined)
    setMaxDate(current?.max != null ? String(current.max) : undefined)
  }, [current?.min, current?.max])

  function validateDates(): boolean {
    if (minDate && maxDate) {
      const min = new Date(minDate)
      const max = new Date(maxDate)
      if (min > max) {
        setError('Start date must be before end date')
        return false
      }
    }
    setError(null)
    return true
  }

  function saveFilters() {
    if (!validateDates()) return

    if (minDate || maxDate) {
      // RangeFilter type expects number but date filters store strings (YYYY-MM-DD)
      // This cast maintains backward compatibility with the existing FilterStore API
      setPendingRangeFilters(
        filterKey,
        minDate as unknown as number | undefined,
        maxDate as unknown as number | undefined
      )
    }

    setOpen(false)
  }

  const clearFilters = () => {
    setMinDate(undefined)
    setMaxDate(undefined)
    setError(null)
    clearPendingRangeFilter(filterKey)
  }

  const getLabel = (): string => {
    if (!isRangeSet) {
      return 'All'
    }

    const min = current?.min ?? null
    const max = current?.max ?? null

    if (min === null && max !== null) {
      return `≤  ${max}`
    }

    if (max === null && min !== null) {
      return `≥  ${min}`
    }

    return `${min}  -  ${max}`
  }

  const isRangeSet = current?.min != null || current?.max != null

  useEffect(() => {
    registerFilterTitle(filterKey, title)
  }, [])

  return (
    <DropdownMenu open={open} onOpenChange={(open) => setOpen(open)}>
      <div className={'relative'}>
        <DropdownMenuTrigger asChild>
          <FilterButton
            open={open}
            isActive={isRangeSet}
            title={title}
            activeValueLabel={getLabel()}
            onClick={() => setOpen(!open)}
            twoLineLayout
          />
        </DropdownMenuTrigger>
        <FilterCloseButton
          isActive={isRangeSet}
          onClick={() => clearFilters()}
        />
      </div>

      <DropdownMenuContent
        forceMount={true}
        side={'bottom'}
        className={cn('bg-white w-[260px] lg:w-[235px] p-[12px]')}
        sideOffset={5}
      >
        <div className="flex gap-[12px] flex-col">
          <DatePicker
            label="Start Date"
            value={minDate}
            onChange={(value) => {
              setMinDate(value || undefined)
              setError(null)
            }}
            allowTyping
          />
          <DatePicker
            label="End Date"
            value={maxDate}
            onChange={(value) => {
              setMaxDate(value || undefined)
              setError(null)
            }}
            allowTyping
          />
          {error && <p className="text-red-500 text-sm">{error}</p>}
        </div>

        <FbButton
          className="mt-[16px] mb-[6px] w-full"
          onClick={saveFilters}
          disabled={!minDate && !maxDate}
        >
          Apply
        </FbButton>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
